import { makeAutoObservable, runInAction } from 'mobx'
import { decorate, injectable, inject } from 'inversify'
import { debounce } from 'lodash'
import moment from 'moment'

import { Api } from '@evertel/api'
import { APIDataAgreementAcceptance } from '@evertel/types'
import { downloadCSVFile } from '@evertel/utils'

// The log displays acceptance times in UTC, so the pickers select UTC wall-clock
// times. This reinterprets the picked fields (which arrive as local wall-clock on
// a JS Date) as UTC (offset 0, keepLocalTime) to produce the instant the API filters
// on. Without this, a picked "15:00" would be shifted by the viewer's offset.
const pickerDateToUTC = (date: Date | null) =>
    date ? moment(date).utcOffset(0, true).toDate() : null

class AgreementsLogController {
    acceptances: APIDataAgreementAcceptance[] = []
    acceptancesCount = 0
    departmentId: number | null = null
    agreementId: number | null = null
    searchTerm = ''
    startDate: Date | null = null
    endDate: Date | null = null
    page = 1
    limit = 25
    isLoading = false
    sortBy = 'createdDate'
    sortOrder: 'asc' | 'desc' = 'desc'

    constructor(
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init(departmentId: number) {
        this.departmentId = departmentId
        this.resetState()
    }

    setSearchTerm(value: string) {
        this.resetMeta()
        this.searchTerm = value
    }

    setAgreementId(value: number | null) {
        this.resetMeta()
        this.agreementId = value
    }

    setStartDate(value: Date | null) {
        this.resetMeta()
        this.startDate = pickerDateToUTC(value)
    }

    setEndDate(value: Date | null) {
        this.resetMeta()
        this.endDate = pickerDateToUTC(value)
    }

    setPage(page: number) {
        this.page = page
    }

    setLimit(limit: number) {
        this.limit = limit
        this.page = 1
    }

    setSort(sortBy: string, sortOrder: 'asc' | 'desc') {
        this.sortBy = sortBy
        this.sortOrder = sortOrder
        this.page = 1
    }

    private debouncedSearchInternal = debounce(() => this.search(), 300)

    debouncedSearch = () => {
        return this.debouncedSearchInternal()
    }

    search = async () => {
        if (!this.departmentId) return

        this.isLoading = true

        try {
            if (!this.acceptancesCount) {
                await this.fetchCount()
            }

            let results: APIDataAgreementAcceptance[] = []

            if (this.acceptancesCount > 0) {
                results = await this.api.Routes.Department.getAgreementAcceptancesSearch(this.departmentId, this.filter)
            }

            runInAction(() => {
                this.acceptances = results
            })
        } catch (e) {
            console.error('AgreementsLog search failed', e)
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    fetchCount = async () => {
        if (!this.departmentId) return 0

        const res = await this.api.Routes.Department.getAgreementAcceptancesSearchCount(
            this.departmentId,
            this.filterWhere
        )

        runInAction(() => {
            this.acceptancesCount = res?.count ?? 0
        })

        return res?.count ?? 0
    }

    exportCSV = async () => {
        if (!this.departmentId) return

        const csv = await this.api.Routes.Department.getAgreementAcceptancesExport(
            this.departmentId,
            this.searchTerm || undefined,
            this.startDate ?? undefined,
            this.endDate ?? undefined,
            this.agreementId ?? undefined
        )

        downloadCSVFile(csv as unknown as Blob, 'agreement-acceptances')
    }

    resetMeta() {
        this.page = 1
        this.acceptances = []
        this.acceptancesCount = 0
    }

    resetState() {
        this.resetMeta()
        this.searchTerm = ''
        this.agreementId = null
        this.startDate = null
        this.endDate = null
    }

    get filterWhere() {
        const where: any = {}

        if (this.searchTerm.trim()) {
            where.searchTerm = this.searchTerm.trim()
        }
        if (this.startDate) {
            where.startDate = this.startDate
        }
        if (this.endDate) {
            where.endDate = this.endDate
        }
        if (this.agreementId) {
            where.departmentAgreementId = this.agreementId
        }

        return where
    }

    get filter() {
        return {
            where: this.filterWhere,
            limit: this.limit,
            skip: (this.page - 1) * this.limit,
            order: this.sortBy && this.sortOrder
                ? `${this.sortBy} ${this.sortOrder.toUpperCase()}`
                : undefined,
            include: ['acceptedBy', 'departmentAgreement', 'agreementDocument', 'room']
        }
    }
}

decorate(injectable(), AgreementsLogController)
decorate(inject(Api), AgreementsLogController, 0)

export { AgreementsLogController }
